import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface part {
  titre: string;
  numberOfQuestions: number;
  numberOfAnswers: number;
}

interface Question {
  questionNumber: number;
  numberOfAnswers: number;
  partIndex: number;
}

interface LocationState {
  testTitle: string;
  parts: part[];
  isEditMode?: boolean;
  templateId?: string;
  isPublic?: boolean;
}

const ConfigureTemplate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [testTitle, setTestTitle] = useState<string>('');
  const [parts, setparts] = useState<part[]>([]);
  const [expandedparts, setExpandedparts] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    // Si pas de données, rediriger vers create-template
    if (!state || !state.testTitle || !state.parts || state.parts.length === 0) {
      navigate('/create-template');
      return;
    }

    // Initialiser les questions avec le nombre de réponses par défaut selon les parts
    setTestTitle(state.testTitle);
    setparts(state.parts);

    // Initialiser toutes les parts comme ouvertes par défaut
    const initialExpanded: { [key: number]: boolean } = {};
    state.parts.forEach((_, index) => {
      initialExpanded[index] = true;
    });
    setExpandedparts(initialExpanded);

    const initialQuestions: Question[] = [];
    let questionNumber = 1;

    // Générer les questions pour chaque part
    state.parts.forEach((part, partIndex) => {
      for (let i = 0; i < part.numberOfQuestions; i++) {
        initialQuestions.push({
          questionNumber: questionNumber++,
          numberOfAnswers: part.numberOfAnswers,
          partIndex: partIndex
        });
      }
    });

    setQuestions(initialQuestions);
  }, [state, navigate]);

  const handleBack = (): void => {
    navigate('/create-template');
  };

  const togglepart = (partIndex: number): void => {
    setExpandedparts(prev => ({
      ...prev,
      [partIndex]: !prev[partIndex]
    }));
  };

  const handleAnswersChange = (questionNumber: number, value: string): void => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 2 && numValue <= 10) {
      setQuestions(prev =>
        prev.map(q =>
          q.questionNumber === questionNumber
            ? { ...q, numberOfAnswers: numValue }
            : q
        )
      );
    }
  };

  const handleValidate = async (): Promise<void> => {
    console.log('=== DÉBUT DE LA VALIDATION DU TEMPLATE ===');

    const token = localStorage.getItem('authToken');
    console.log('Token:', token ? 'Présent' : 'Absent');

    if (!token) {
      navigate('/login');
      return;
    }

    // Calculer le nombre de questions par défaut (le plus courant)
    const answerCounts: { [key: number]: number } = {};
    questions.forEach(q => {
      answerCounts[q.numberOfAnswers] = (answerCounts[q.numberOfAnswers] || 0) + 1;
    });
    const defaultAnswers = parseInt(Object.keys(answerCounts).reduce((a, b) =>
      answerCounts[parseInt(a)] > answerCounts[parseInt(b)] ? a : b
    ));

    const payload = {
      templateTitle: testTitle,
      numberOfQuestions: questions.length,
      numberOfAnswers: defaultAnswers,
      questions: questions,
      parts: parts,
      isPublic: state.isPublic || false
    };

    console.log('=== PAYLOAD DÉTAILLÉ ===');
    console.log('Payload complet:', JSON.stringify(payload, null, 2));
    console.log('Nombre total de questions:', questions.length);
    console.log('Nombre de parts:', parts.length);
    console.log('isPublic:', state.isPublic);
    console.log('Mode:', state.isEditMode ? 'Édition' : 'Création');
    console.log('Exemples de questions avec parts:');
    questions.slice(0, 5).forEach(q => {
      console.log(`  Q${q.questionNumber}: ${q.numberOfAnswers} réponses, part ${q.partIndex}`);
    });

    try {
      const url = state.isEditMode
        ? `${API_BASE_URL}/api/templates/${state.templateId}`
        : `${API_BASE_URL}/api/templates/create`;

      const method = state.isEditMode ? 'PUT' : 'POST';

      console.log(`Envoi de la requête ${method} à ${url}`);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      console.log('Réponse reçue, status:', response.status);
      console.log('Response OK?:', response.ok);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur du serveur:', errorData);
        throw new Error(errorData.error || t('templates.configure.errors.createTemplate'));
      }

      const data = await response.json();
      console.log('Template créé avec succès:', data);

      // Rediriger vers mes tests
      navigate('/my-tests');

    } catch (error) {
      console.error('=== ERREUR COMPLÈTE ===');
      console.error('Type:', error);
      console.error('Message:', error instanceof Error ? error.message : t('templates.configure.errors.unknownError'));
      console.error('Stack:', error instanceof Error ? error.stack : 'N/A');
    }
  };

  if (!state || questions.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{t('templates.configure.title', { title: testTitle })}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('templates.configure.pageTitle')}
            </h2>
            <p className="text-gray-600">
              {t('templates.configure.description')}
            </p>
          </div>

          {/* Tableau des questions organisé par parts */}
          <div className="space-y-6">
            {parts.map((part, partIndex) => {
              const partQuestions = questions.filter(q => q.partIndex === partIndex);

              const isExpanded = expandedparts[partIndex];

              return (
                <div key={partIndex} className="border border-gray-300 rounded-lg overflow-hidden">
                  {/* En-tête de la part */}
                  <button
                    type="button"
                    onClick={() => togglepart(partIndex)}
                    className="w-full bg-blue-600 px-4 py-3 hover:bg-blue-700 transition flex items-center justify-between"
                  >
                    <div className="text-left">
                      <h3 className="text-white font-bold text-lg">
                        {t('templates.configure.partHeader', { number: partIndex + 1, title: part.titre })}
                      </h3>
                      <p className="text-blue-100 text-sm">
                        {t('templates.configure.partInfo', { questions: part.numberOfQuestions, answers: part.numberOfAnswers })}
                      </p>
                    </div>
                    <div className="text-white">
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </div>
                  </button>

                  {/* Tableau des questions de cette part */}
                  {isExpanded && (
                    <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-800">
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.answers')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.answers')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.answers')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.questionNumber')}
                          </th>
                          <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                            {t('templates.configure.tableHeaders.answers')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Diviser les questions en 4 groupes égaux
                          const quarterSize = Math.ceil(partQuestions.length / 4);
                          const group1 = partQuestions.slice(0, quarterSize);
                          const group2 = partQuestions.slice(quarterSize, quarterSize * 2);
                          const group3 = partQuestions.slice(quarterSize * 2, quarterSize * 3);
                          const group4 = partQuestions.slice(quarterSize * 3);

                          // Nombre de lignes = taille du plus grand groupe
                          const maxRows = Math.max(group1.length, group2.length, group3.length, group4.length);

                          return Array.from({ length: maxRows }).map((_, rowIndex) => {
                            const q1 = group1[rowIndex];
                            const q2 = group2[rowIndex];
                            const q3 = group3[rowIndex];
                            const q4 = group4[rowIndex];

                            const renderQuestion = (q: typeof q1) => {
                              if (!q) {
                                return (
                                  <>
                                    <td className="border border-gray-300 px-2 py-2"></td>
                                    <td className="border border-gray-300 px-2 py-2"></td>
                                  </>
                                );
                              }

                              return (
                                <>
                                  <td className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-semibold text-sm">
                                    Q{q.questionNumber}
                                  </td>
                                  <td className="border border-gray-300 px-2 py-2 text-center">
                                    <input
                                      type="number"
                                      value={q.numberOfAnswers}
                                      onChange={(e) => handleAnswersChange(q.questionNumber, e.target.value)}
                                      min="2"
                                      max="10"
                                      className="w-14 px-1 py-1 border border-gray-300 rounded text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </td>
                                </>
                              );
                            };

                            return (
                              <tr
                                key={rowIndex}
                                className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-blue-50`}
                              >
                                {renderQuestion(q1)}
                                {renderQuestion(q2)}
                                {renderQuestion(q3)}
                                {renderQuestion(q4)}
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bouton Valider */}
          <div className="flex justify-end mt-8">
            <button
              onClick={handleValidate}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-8 rounded-lg transition"
            >
              <Check className="w-5 h-5" />
              {t('templates.configure.validateButton')}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConfigureTemplate;
